/**
 * サブスクリプションページコンポーネント
 *
 * プラン選択とStripe決済
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { loadStripe } from '@stripe/stripe-js';
import { createCheckoutSession, createPortalSession } from '../../services/firebase';
import { useT } from '../../i18n';

// Stripe公開キー（環境変数から取得）
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Stripeインスタンスをキャッシュ
let stripePromise = null;
function getStripe() {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
}

// プラン情報
const PLANS = {
  free: {
    name: 'Free',
    price: 0,
    priceLabel: '無料',
    features: [
      { text: '保存ページ: 500件', included: true },
      { text: 'AI検索: 月5回', included: true }
    ]
  },
  pro: {
    name: 'Pro',
    price: 4,
    priceLabel: '$4.00/月',
    features: [
      { text: '保存ページ: 3,000件', included: true },
      { text: 'AI検索: 月20回', included: true }
    ]
  }
};

// Coming Soonプラン
const COMING_SOON_PLANS = [
  {
    name: 'VIP',
    priceLabel: '$12.00/月',
    description: '広告なし・優先サポート・データエクスポート'
  },
  {
    name: 'Star',
    priceLabel: '$30.00/月',
    description: 'チーム・ビジネス向け'
  }
];

function SubscriptionPage() {
  const { user } = useAuth();
  const { userData, isPremium } = useUser();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchParams] = useSearchParams();
  const { t } = useT();

  // URLパラメータでチェックアウト結果を確認
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setMessage({ type: 'success', text: t('subscription.success') });
    } else if (searchParams.get('canceled') === 'true') {
      setMessage({ type: 'info', text: t('subscription.canceled') });
    }
  }, [searchParams, t]);

  /**
   * Stripeチェックアウトを開始
   */
  async function handleSubscribe() {
    console.log('[SubscriptionPage] handleSubscribe開始');
    console.log('[SubscriptionPage] STRIPE_PUBLISHABLE_KEY:', STRIPE_PUBLISHABLE_KEY ? '設定済み' : '未設定');

    if (!STRIPE_PUBLISHABLE_KEY) {
      setMessage({ type: 'error', text: t('subscription.stripeNotConfigured') });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      console.log('[SubscriptionPage] createCheckoutSession呼び出し中...');
      console.log('[SubscriptionPage] origin:', window.location.origin);

      // Cloud Functionを呼び出してチェックアウトセッションを作成
      const result = await createCheckoutSession({
        origin: window.location.origin
      });

      console.log('[SubscriptionPage] createCheckoutSession結果:', result);
      const { url, sessionId } = result.data;

      // Stripe Checkout URLにリダイレクト
      if (url) {
        window.location.href = url;
      } else if (sessionId) {
        // フォールバック: Stripe.jsでリダイレクト
        const stripe = await getStripe();
        if (stripe) {
          const { error } = await stripe.redirectToCheckout({ sessionId });
          if (error) {
            throw new Error(error.message);
          }
        }
      }

    } catch (error) {
      console.error('Stripeエラー:', error);
      console.error('エラー詳細:', {
        code: error.code,
        message: error.message,
        details: error.details
      });
      // Cloud Functionsからのエラーメッセージを詳細に表示
      const errorMessage = error.message || error.code || t('subscription.checkoutError');
      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  }

  /**
   * サブスクリプションを管理（キャンセルなど）
   */
  async function handleManageSubscription() {
    setLoading(true);
    setMessage(null);

    try {
      // Cloud Functionを呼び出してポータルセッションを作成
      const result = await createPortalSession({
        origin: window.location.origin
      });

      const { url } = result.data;

      if (url) {
        window.location.href = url;
      }

    } catch (error) {
      console.error('ポータルエラー:', error);
      setMessage({
        type: 'error',
        text: error.message || t('subscription.portalError')
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fade-in max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {t('subscription.title')}
        </h1>
        <p className="text-gray-500">
          {t('subscription.subtitle')}
        </p>
      </div>

      {/* メッセージ表示 */}
      {message && (
        <div className={`mb-6 p-4 rounded-xl text-center ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
          'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* 現在のプラン表示 */}
      {isPremium && (
        <div className="bg-gradient-to-r from-primary to-blue-600 text-white rounded-2xl p-4 mb-8 text-center">
          <p className="font-bold">現在 Pro プランをご利用中です</p>
          <button
            onClick={handleManageSubscription}
            className="mt-2 text-sm underline hover:no-underline"
          >
            サブスクリプションを管理
          </button>
        </div>
      )}

      {/* プランカード */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Freeプラン */}
        <div className={`card border-2 ${!isPremium ? 'border-primary' : 'border-gray-200'}`}>
          {!isPremium && (
            <div className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
              現在のプラン
            </div>
          )}
          <h2 className="text-xl font-bold text-gray-900">{PLANS.free.name}</h2>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {PLANS.free.priceLabel}
          </p>
          <ul className="mt-6 space-y-3">
            {PLANS.free.features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2">
                <span className={feature.included ? 'text-green-500' : 'text-gray-300'}>
                  {feature.included ? '✓' : '✕'}
                </span>
                <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                  {feature.text}
                </span>
              </li>
            ))}
          </ul>
          <button
            disabled
            className="btn-secondary w-full mt-6 opacity-50 cursor-not-allowed"
          >
            {isPremium ? 'ダウングレード' : '現在のプラン'}
          </button>
        </div>

        {/* Proプラン */}
        <div className={`card border-2 ${isPremium ? 'border-primary' : 'border-gray-200'} relative overflow-hidden`}>
          {/* おすすめバッジ */}
          <div className="absolute top-0 right-0 bg-gradient-to-r from-primary to-blue-600 text-white text-xs font-bold px-4 py-1 rounded-bl-xl">
            おすすめ
          </div>

          {isPremium && (
            <div className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
              現在のプラン
            </div>
          )}
          <h2 className="text-xl font-bold text-gray-900">{PLANS.pro.name}</h2>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {PLANS.pro.priceLabel}
          </p>
          <ul className="mt-6 space-y-3">
            {PLANS.pro.features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2">
                <span className={feature.included ? 'text-green-500' : 'text-gray-300'}>
                  {feature.included ? '✓' : '✕'}
                </span>
                <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                  {feature.text}
                </span>
              </li>
            ))}
          </ul>
          {isPremium ? (
            <button
              onClick={handleManageSubscription}
              className="btn-secondary w-full mt-6"
            >
              サブスクリプションを管理
            </button>
          ) : (
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="btn-primary w-full mt-6"
            >
              {loading ? '処理中...' : 'Proにアップグレード'}
            </button>
          )}
        </div>
      </div>

      {/* Coming Soon プラン */}
      <div className="mt-8">
        <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">
          Coming Soon
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {COMING_SOON_PLANS.map((plan) => (
            <div
              key={plan.name}
              className="card border-2 border-gray-200 opacity-60 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 bg-gray-400 text-white text-xs font-bold px-4 py-1 rounded-bl-xl">
                準備中
              </div>
              <h3 className="text-xl font-bold text-gray-500">{plan.name}</h3>
              <p className="text-2xl font-bold text-gray-400 mt-2">
                {plan.priceLabel}
              </p>
              <p className="text-sm text-gray-400 mt-2">
                {plan.description}
              </p>
              <button
                disabled
                className="btn-secondary w-full mt-6 opacity-50 cursor-not-allowed"
              >
                近日公開
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <section className="mt-12">
        <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">
          よくある質問
        </h2>
        <div className="space-y-4">
          <details className="card">
            <summary className="font-medium text-gray-900 cursor-pointer">
              いつでもキャンセルできますか？
            </summary>
            <p className="mt-3 text-gray-600 text-sm">
              はい、いつでもキャンセル可能です。キャンセル後も、現在の請求期間が終了するまでプレミアム機能をご利用いただけます。
            </p>
          </details>
          <details className="card">
            <summary className="font-medium text-gray-900 cursor-pointer">
              支払い方法は何がありますか？
            </summary>
            <p className="mt-3 text-gray-600 text-sm">
              クレジットカード（Visa、Mastercard、American Express）に対応しています。決済はStripeを通じて安全に処理されます。
            </p>
          </details>
          <details className="card">
            <summary className="font-medium text-gray-900 cursor-pointer">
              ダウングレードしたらデータはどうなりますか？
            </summary>
            <p className="mt-3 text-gray-600 text-sm">
              保存したページは削除されません。ただし、Freeプランの制限（500件）を超えている場合、新しいページの保存ができなくなります。
            </p>
          </details>
        </div>
      </section>
    </div>
  );
}

export default SubscriptionPage;
